import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import logo from '../assets/img/logo.png';
import apiService from '../api/apiService';
import SearchModal from './SearchModal';

const Navigation = ({ selectedCategory, onCategoryClick, onAddToOrder }) => {
  const [categories, setCategories] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesData = await apiService.getCategories();
        setCategories(categoriesData);

        const itemsPromises = categoriesData.map(category => 
          apiService.getItemsByCategory(category._id)
        );
        const itemsResults = await Promise.all(itemsPromises);
        const allItems = itemsResults.flat();
        setMenuItems(allItems);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const checkForScrollPosition = () => {
      const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
      setShowLeftArrow(hasHorizontalOverflow && container.scrollLeft > 0);
      setShowRightArrow(
        hasHorizontalOverflow && 
        container.scrollLeft < container.scrollWidth - container.clientWidth
      );
    };

    checkForScrollPosition();
    container.addEventListener('scroll', checkForScrollPosition);
    const resizeObserver = new ResizeObserver(checkForScrollPosition);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', checkForScrollPosition);
      resizeObserver.disconnect();
    };
  }, [categories]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      const targetScroll = container.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 shadow-sm bg-white/95 backdrop-blur-md">
      {/* Desktop Navigation */}
      <div className="hidden md:block">
        <div className="container px-4 mx-auto">
          <div className="flex items-center justify-between h-20">
            {/* Logo Section */}
            <div className="flex items-center">
              <img src={logo} alt="Logo" className="w-auto h-12" />
              <div className="ml-3">
                <h1 className="text-2xl font-bold text-amber-900">Mr WoodFire</h1>
                <div className="flex items-center mt-1 text-sm text-amber-700">
                  <MapPin className="w-4 h-4 mr-1" />
                  Railway avenue Werribee, VIC
                </div>
              </div>
            </div>

            {/* Search in middle */}
            <div className="absolute transform -translate-x-1/2 left-1/2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Menu"
                  className="w-64 px-4 py-2 pl-10 transition-all duration-200 bg-white border-2 shadow-sm border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  onClick={() => setIsSearchOpen(true)}
                />
                <Search className="absolute w-4 h-4 transform -translate-y-1/2 text-amber-500 left-3 top-1/2" />
              </div>
            </div>

            {/* Right space keeper */}
            <div className="w-[200px]"></div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img src={logo} alt="Logo" className="w-8 h-8" />
              <div className="ml-2">
                <h1 className="text-base font-bold text-amber-900">Mr WoodFire</h1>
                <div className="flex items-center text-xs text-amber-700">
                  <MapPin className="w-3 h-3 mr-1" />
                  Railway avenue Werribee, VIC
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 rounded-full text-amber-600 bg-amber-50"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Categories Navigation */}
      <div className="bg-white/95 backdrop-blur-md">
        <div className="container relative px-4 mx-auto">
          {showLeftArrow && (
            <button 
              onClick={() => scroll('left')}
              className="absolute left-0 z-10 flex items-center justify-center w-8 h-8 transition-colors -translate-y-1/2 bg-white rounded-full shadow-lg top-1/2 hover:bg-amber-50"
            >
              <ChevronLeft className="w-5 h-5 text-amber-700" />
            </button>
          )}

          <div 
            ref={scrollContainerRef}
            className="flex items-center px-4 py-2 space-x-8 overflow-x-auto scrollbar-hide"
          >
            {categories.map((category) => (
              <button
                key={category._id}
                onClick={() => onCategoryClick(category.label)}
                className={`whitespace-nowrap text-sm pb-2 -mb-2 font-bold transition-all duration-200 ${
                  selectedCategory === category.label
                    ? 'text-amber-700 border-b-[3px] border-amber-500'
                    : 'text-gray-600 hover:text-amber-700'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          {showRightArrow && (
            <button 
              onClick={() => scroll('right')}
              className="absolute right-0 z-10 flex items-center justify-center w-8 h-8 transition-colors -translate-y-1/2 bg-white rounded-full shadow-lg top-1/2 hover:bg-amber-50"
            >
              <ChevronRight className="w-5 h-5 text-amber-700" />
            </button>
          )}
        </div>
      </div>

      {/* Search Modal */}
      <SearchModal 
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        menuItems={menuItems}
        onAddToOrder={onAddToOrder}
      />
    </header>
  );
};

export default Navigation;